<script setup lang="ts">
import type { PropType } from 'vue';
import type { EngineError } from '#layers/base/types/error';
import { createReusableTemplate, unrefElement } from '@vueuse/core';
import { computed, nextTick, useTemplateRef, watch } from 'vue';
import { ensureArray } from '#layers/base/utils/array';
import { toStructuredError } from '#layers/base/utils/error';
import { isNullishOrEmpty } from '#layers/base/utils/iterable';
import { useErrorFormatter } from '~/composables/use-error-formatter';

defineOptions({
	inheritAttrs: false,
});

const props = defineProps<{
	error: Error | { data: Error } | any;
	class?: string;
	showCopyButton?: boolean;
}>();

const normalizedErrors = computed(() => ensureArray(props.error).filter((error: any) => !!error).map((error: any) => toStructuredError(error)));
const hasManyErrors = computed(() => normalizedErrors.value.length > 1);

const { formatError } = useErrorFormatter();

const alertRef = useTemplateRef<HTMLElement>('alert');

watch(() => props.error, (newError) => {
	if (newError) {
		nextTick(() => {
			const el = unrefElement(alertRef);
			el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		});
	}
}, { immediate: true });

const [DefineMessage, ReuseMessage] = createReusableTemplate({
	props: {
		error: Object as PropType<EngineError | any>,
	},
});
</script>

<!-- eslint-disable vue/no-template-shadow -->
<template>
	<DefineMessage v-slot="{ error }">
		<div>
			{{ formatError(error) }}
			<div v-if="error.source" class="mt-1.5 flex">
				<div class="border-l-2 border-error" />
				<ReuseMessage :error="error.source" class="ml-3" />
			</div>
		</div>
	</DefineMessage>

	<DNotice
		v-if="!isNullishOrEmpty(normalizedErrors)"
		ref="alert"
		color="error"
		class="scroll-mt-4"
		role="alert"
		data-test-id="error-alert"
		:class="props.class"
		v-bind="$attrs"
	>
		<template #description>
			<ul class="space-y-2" :class="{ 'ms-3 list-disc marker:text-error': hasManyErrors }">
				<li v-for="(error, index) in normalizedErrors" :key="index">
					<ReuseMessage :error />
				</li>
			</ul>
			<DCopyButton
				v-if="showCopyButton"
				class="absolute inset-e-3 top-3"
				:value="JSON.stringify(hasManyErrors ? normalizedErrors : normalizedErrors[0], null, 2)"
				color="error"
				variant="link"
			/>
		</template>
	</DNotice>
</template>
